import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Form,
  Button,
  Spinner,
  Row,
  Col,
  Alert,
  Table,
  Modal,
  Badge,
} from 'react-bootstrap';
import {
  BsEye,
  BsPencilSquare,
  BsSave,
  BsTrash,
} from 'react-icons/bs';
import api from '../lib/api';

const tonePresets = {
  professional: `- Be concise and clear.\n- Avoid humor or filler.\n- Use formal business tone.`,
  friendly: `- Use casual and warm language.\n- Be supportive and approachable.\n- Keep responses easy to understand.`,
  formal: `- Use polite and proper grammar.\n- Avoid contractions.\n- Maintain an academic or official tone.`,
};

const MODELS = ['mistral', 'tinyllama', 'openai', 'gemini', 'groq'];
const USE_CASES = ['default', 'support', 'sales', 'developer'];
const TONES = ['custom', 'professional', 'friendly', 'formal'];

const normalizeModel = (value) => (value || '').toString().trim().toLowerCase();
const normalizeUseCase = (value) => (value || '').toString().trim().toLowerCase();

function PanelBlock({ title, subtitle, children, className = '' }) {
  return (
    <Card
      className={`border-0 rounded-4 h-100 ${className}`}
      style={{ background: '#f8fafc' }}
    >
      <Card.Body className="p-3 p-md-4">
        <div className="fw-semibold mb-1" style={{ color: '#0f172a', fontSize: '1rem' }}>
          {title}
        </div>
        {subtitle ? (
          <div className="text-muted small mb-3" style={{ lineHeight: 1.55 }}>
            {subtitle}
          </div>
        ) : null}
        {children}
      </Card.Body>
    </Card>
  );
}

function AdminInstructionsCard() {
  const [instructions, setInstructions] = useState([]);
  const [activeModel, setActiveModel] = useState('mistral');
  const [current, setCurrent] = useState({
    model: 'mistral',
    use_case: 'default',
    tone: 'custom',
    content: '',
    updated_at: null,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState('');
  const [question, setQuestion] = useState('What is your refund policy?');
  const [saveMsg, setSaveMsg] = useState('');
  const [saveErr, setSaveErr] = useState('');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const currentInstructionKey = `${current.model}-${current.use_case}`;
  const totalInstructionSets = instructions.length;

  useEffect(() => {
    (async () => {
      try {
        const [instructionRes, modelRes] = await Promise.all([
          api.get('/chat-instructions/'),
          api.get('/chat-model/'),
        ]);
        const data = Array.isArray(instructionRes.data) ? instructionRes.data : [];
        const active = normalizeModel(modelRes?.data?.current_model) || 'mistral';
        setInstructions(data);
        setActiveModel(active);
        setCurrent((prev) => ({
          ...prev,
          model: active,
        }));
      } catch {
        setSaveErr('Unable to load instruction sets.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const found = instructions.find(
      (i) =>
        normalizeModel(i.model) === normalizeModel(current.model) &&
        normalizeUseCase(i.use_case) === normalizeUseCase(current.use_case)
    );

    if (found) {
      setCurrent({
        model: found.model,
        use_case: found.use_case,
        tone: found.tone || 'custom',
        content: found.content || '',
        updated_at: found.updated_at || null,
      });
    } else {
      setCurrent((prev) => ({
        ...prev,
        content: '',
        tone: 'custom',
        updated_at: null,
      }));
    }
  }, [instructions, current.model, current.use_case]);

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg('');
    setSaveErr('');

    try {
      const res = await api.post('/chat-instructions/', {
        model: normalizeModel(current.model),
        use_case: normalizeUseCase(current.use_case),
        tone: current.tone,
        content: current.content,
      });

      const now = new Date().toISOString();
      const saved = {
        model: normalizeModel(current.model),
        use_case: normalizeUseCase(current.use_case),
        tone: current.tone,
        content: current.content,
        updated_at: now,
      };

      const idx = instructions.findIndex(
        (i) =>
          normalizeModel(i.model) === normalizeModel(current.model) &&
          normalizeUseCase(i.use_case) === normalizeUseCase(current.use_case)
      );

      if (idx >= 0) {
        const next = [...instructions];
        next[idx] = { ...instructions[idx], ...saved };
        setInstructions(next);
      } else {
        setInstructions((prev) => [...prev, { id: res?.data?.id, ...saved }]);
      }

      setCurrent((prev) => ({ ...prev, updated_at: now }));
      setSaveMsg(res?.data?.message || 'Instruction saved successfully.');
      setTimeout(() => setSaveMsg(''), 1800);
    } catch (err) {
      setSaveErr(
        err?.response?.data?.error ||
          err?.response?.data?.detail ||
          err?.message ||
          'Error saving instructions'
      );
      setTimeout(() => setSaveErr(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  const applyTonePreset = (tone) => {
    setCurrent((prev) => ({
      ...prev,
      tone,
      content: tonePresets[tone] || '',
    }));
  };

  const handlePreview = () => {
    const fullPrompt = `
You are an assistant trained to respond like this:

Instructions from admin:
${current.content}

User question: ${question}
Answer:`.trim();

    setPreview(fullPrompt);
  };

  const handleOpenDeleteModal = (instruction) => {
    setDeleteTarget(instruction);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    if (deleting) return;
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setSaveErr('');
    setSaveMsg('');

    try {
      const hasId = !!deleteTarget.id;

      if (hasId) {
        await api.delete(`/chat-instructions/${deleteTarget.id}/`);
      }

      setInstructions((prev) =>
        prev.filter((i) =>
          hasId
            ? i.id !== deleteTarget.id
            : !(i.model === deleteTarget.model && i.use_case === deleteTarget.use_case)
        )
      );

      if (
        deleteTarget.model === current.model &&
        deleteTarget.use_case === current.use_case
      ) {
        setCurrent((prev) => ({
          ...prev,
          content: '',
          tone: 'custom',
          updated_at: null,
        }));
      }

      setSaveMsg('Instruction deleted successfully.');
      setTimeout(() => setSaveMsg(''), 1800);
    } catch (err) {
      setSaveErr(
        err?.response?.data?.error ||
          err?.response?.data?.detail ||
          err?.message ||
          'Error deleting instruction'
      );
      setTimeout(() => setSaveErr(''), 3000);
    } finally {
      setDeleting(false);
      handleCloseDeleteModal();
    }
  };

  const statusBadge = useMemo(() => {
    const currentExists = instructions.some(
      (i) =>
        normalizeModel(i.model) === normalizeModel(current.model) &&
        normalizeUseCase(i.use_case) === normalizeUseCase(current.use_case)
    );
    return currentExists
      ? { label: 'Existing Set', bg: 'success' }
      : { label: 'New Set', bg: 'secondary' };
  }, [instructions, current.model, current.use_case]);

  const runtimeStatus = useMemo(() => {
    const existsForActiveModel = instructions.some(
      (i) =>
        normalizeModel(i.model) === normalizeModel(activeModel) &&
        normalizeUseCase(i.use_case) === normalizeUseCase(current.use_case)
    );
    return {
      existsForActiveModel,
      sameAsEditorModel: normalizeModel(current.model) === normalizeModel(activeModel),
    };
  }, [instructions, activeModel, current.model, current.use_case]);

  if (loading) {
    return (
      <Card className="admin-surface-card h-100">
        <Card.Body className="text-center py-5">
          <Spinner animation="border" />
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Card className="admin-surface-card h-100 border-0 shadow-sm rounded-4 overflow-hidden">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div>
              <div className="admin-section-title mb-1">AI Instruction Manager</div>
              <p className="text-muted small mb-0" style={{ maxWidth: 760 }}>
                Create and manage model-specific instruction .
              </p>
            </div>

            <div className="d-flex flex-wrap gap-2">
              <Badge bg="light" text="dark" pill className="px-3 py-2 border">
                Total Sets: {totalInstructionSets}
              </Badge>
              <Badge bg={statusBadge.bg} pill className="px-3 py-2">
                {statusBadge.label}
              </Badge>
            </div>
          </div>

          {saveMsg ? <Alert variant="success" className="rounded-4">{saveMsg}</Alert> : null}
          {saveErr ? <Alert variant="danger" className="rounded-4">{saveErr}</Alert> : null}
          <Alert
            variant={runtimeStatus.existsForActiveModel ? 'success' : 'warning'}
            className="rounded-4"
          >
            <div className="fw-semibold mb-1">Runtime model/use-case check</div>
            <div className="small">
              Active model from Channels: <strong>{activeModel}</strong> | Use case: <strong>{current.use_case}</strong>.<br />
              Instruction for runtime pair: <strong>{runtimeStatus.existsForActiveModel ? 'Configured' : 'Missing'}</strong>.
              {!runtimeStatus.sameAsEditorModel ? (
                <>
                  {' '}You are editing <strong>{current.model}</strong>, not the active runtime model.
                </>
              ) : null}
            </div>
          </Alert>

          <Row className="g-4">
            <Col xl={7}>
              <PanelBlock
                title="Instruction Editor"
                subtitle="Select the target model, use case, and tone, then write the instruction content that should guide assistant behavior."
                className="h-100"
              >
                <Row className="g-3 mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Model</Form.Label>
                      <Form.Select
                        value={current.model}
                        onChange={(e) =>
                          setCurrent((prev) => ({ ...prev, model: e.target.value }))
                        }
                        disabled={saving}
                      >
                        {MODELS.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Use Case</Form.Label>
                      <Form.Select
                        value={current.use_case}
                        onChange={(e) =>
                          setCurrent((prev) => ({
                            ...prev,
                            use_case: e.target.value,
                          }))
                        }
                        disabled={saving}
                      >
                        {USE_CASES.map((u) => (
                          <option key={u} value={u}>
                            {u}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Tone</Form.Label>
                      <Form.Select
                        value={current.tone}
                        onChange={(e) => {
                          const tone = e.target.value;
                          if (tone !== 'custom') {
                            applyTonePreset(tone);
                          } else {
                            setCurrent((prev) => ({ ...prev, tone }));
                          }
                        }}
                        disabled={saving}
                      >
                        {TONES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Instruction Text</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={12}
                    value={current.content}
                    onChange={(e) =>
                      setCurrent((prev) => ({
                        ...prev,
                        content: e.target.value,
                      }))
                    }
                    disabled={saving}
                    placeholder="Example: Respond clearly, avoid filler, keep answers practical, and always prioritize customer support accuracy."
                  />
                </Form.Group>

                <div className="small text-muted mb-3">
                  Active key: {currentInstructionKey}
                  <br />
                  Last updated:{' '}
                  {current.updated_at
                    ? new Date(current.updated_at).toLocaleString()
                    : 'N/A'}
                </div>

                <div className="d-flex flex-wrap gap-2">
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={saving}
                    className="admin-action-btn d-inline-flex align-items-center gap-2"
                  >
                    {saving ? <Spinner size="sm" animation="border" /> : <BsSave />}
                    Save Instructions
                  </Button>

                  <Button
                    variant="secondary"
                    onClick={handlePreview}
                    disabled={saving}
                    className="admin-action-btn d-inline-flex align-items-center gap-2"
                  >
                    <BsEye />
                    Preview
                  </Button>
                </div>
              </PanelBlock>
            </Col>

            <Col xl={5}>
              <PanelBlock
                title="All Instruction Sets"
                subtitle="Browse and manage all saved instruction sets. "
                className="h-100"
              >
                <div className="table-responsive" style={{ maxHeight: 620, overflowY: 'auto' }}>
                  <Table hover className="admin-table align-middle mb-0">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Model</th>
                        <th>Use Case</th>
                        <th>Tone</th>
                        <th>Updated</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {instructions.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-4">
                            No instruction sets found
                          </td>
                        </tr>
                      ) : (
                        instructions.map((i) => (
                          <tr key={`${i.id ?? `${i.model}-${i.use_case}`}`}>
                            <td>{i.id ?? '-'}</td>
                            <td>{i.model}</td>
                            <td>{i.use_case}</td>
                            <td>{i.tone || 'custom'}</td>
                            <td>
                              {i.updated_at
                                ? new Date(i.updated_at).toLocaleString()
                                : 'N/A'}
                            </td>
                            <td>
                              <div className="d-flex gap-2 flex-wrap">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="admin-action-btn d-inline-flex align-items-center gap-1"
                                  onClick={() =>
                                    setCurrent((prev) => ({
                                      ...prev,
                                      model: i.model,
                                      use_case: i.use_case,
                                    }))
                                  }
                                >
                                  <BsPencilSquare />
                                  Edit
                                </Button>

                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  className="admin-action-btn d-inline-flex align-items-center gap-1"
                                  onClick={() => handleOpenDeleteModal(i)}
                                >
                                  <BsTrash />
                                  Delete
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </PanelBlock>
            </Col>

            {preview ? (
              <Col xs={12}>
                <PanelBlock
                  title="Prompt Preview"
                  subtitle="Use this preview to inspect how your saved instruction text will frame a sample user request."
                >
                  <Row className="g-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Sample Question</Form.Label>
                        <Form.Control
                          type="text"
                          value={question}
                          onChange={(e) => setQuestion(e.target.value)}
                          disabled={saving}
                          placeholder="Example: What is your refund policy?"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={12}>
                      <div
                        className="p-3 rounded-4"
                        style={{
                          whiteSpace: 'pre-wrap',
                          fontSize: '0.92rem',
                          background: '#ffffff',
                          border: '1px solid #e8edf5',
                          lineHeight: 1.65,
                        }}
                      >
                        {preview}
                      </div>
                    </Col>
                  </Row>
                </PanelBlock>
              </Col>
            ) : null}
          </Row>
        </Card.Body>
      </Card>

      <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Instruction</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteTarget ? (
            <>
              <p>Are you sure you want to delete this instruction set?</p>
              <ul className="mb-0">
                <li>
                  <strong>Model:</strong> {deleteTarget.model}
                </li>
                <li>
                  <strong>Use Case:</strong> {deleteTarget.use_case}
                </li>
                <li>
                  <strong>Tone:</strong> {deleteTarget.tone || 'custom'}
                </li>
              </ul>
            </>
          ) : (
            <p>No instruction selected.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseDeleteModal}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={deleting || !deleteTarget}
          >
            {deleting ? <Spinner size="sm" animation="border" /> : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default AdminInstructionsCard;
